import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../features/cmms/cmmsFeature";
import { getMyWorkOrders } from "../fetchers/cmmsWorkOrder";
import { useLocalStorage } from "./useLocalStorage";

/*
 * Number of work orders currently waiting on the signed-in user — the sidebar badge source.
 *
 * "Mine" is the backend's definition, not a client-side filter: GetMineAsync counts a work
 * order as mine when it is dispatched to me, assigned to me, or Open and belongs to one of
 * my departments. Re-deriving that here would drift the moment the rule changes.
 *
 * The status filter is left off deliberately — /mine defaults to Open + InProgress, which
 * is exactly "still needs doing". Passing statuses explicitly would pin the badge to
 * today's enum values.
 */
export function useMyWorkOrderCount() {
  const { roleIds } = useAuth();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const cmmsEnabled = useIsCmmsEnabled();

  const isCmmsUser = hasAnyRole(roleIds, CMMS_FIELD_ROLES);

  const query = useQuery(
    ["cmmsMyWorkOrders", companyKey],
    () => getMyWorkOrders(companyKey),
    {
      // Users without a CMMS role would get an empty list at best and a 403 at worst.
      // Skipping the request keeps the sidebar quiet for everyone else.
      // The CMMS module switch is checked here too: the sidebar renders on every page,
      // so without it every OEE user in production would hit /api/workorders/mine on every
      // page load for a module they cannot see.
      enabled: cmmsEnabled && isCmmsUser && Boolean(companyKey),
      staleTime: 60000,
      keepPreviousData: true,
    }
  );

  // 0 while loading, so the badge stays hidden rather than flashing a stale or wrong number.
  return Array.isArray(query.data) ? query.data.length : 0;
}

export default useMyWorkOrderCount;
